import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import "./TaskDetailScreen.css";
import logo from "../../Assets/logo.jpg";

const TaskDetailScreen = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);
  const [activeTab, setActiveTab] = useState("completed");

  const [completedTasks, setCompletedTasks] = useState([]);
  const [incompleteTasks, setIncompleteTasks] = useState([]);
  const [missedTasks, setMissedTasks] = useState([]);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totals, setTotals] = useState({ completed: 0, pending: 0, started: 0, missed: 0, total: 0 });

  const fetchUserDetails = useCallback(async (tab, pageNum) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/users/tasks/${userId}?tab=${tab}&page=${pageNum}`);
      const userData = res.data.data;

      setUser(userData);
      setTotalPages(userData.totalPages || 1);
      if (userData.totals) setTotals(userData.totals);

      if (tab === "completed")  setCompletedTasks(userData.tasks || []);
      if (tab === "incomplete") setIncompleteTasks(userData.tasks || []);
      if (tab === "missed")     setMissedTasks(userData.tasks || []);

    } catch (err) {
      console.error("❌ Error fetching task details:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Fetch on tab or page change
  useEffect(() => {
    fetchUserDetails(activeTab, currentPage);
  }, [activeTab, currentPage, fetchUserDetails]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const formatDateTime = (date) =>
    date ? new Date(date).toLocaleString("en-IN") : "—";
  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-IN") : "—";

  const renderCoin = (amount) => (
    <span className="tds-coin">
      <img src={logo} alt="coin" className="tds-coin-icon" loading="lazy" />
      {amount || 0}
    </span>
  );

  const Paginator = ({ page, setPage }) => {
    if (totalPages <= 1) return null;
    return (
      <div className="tds-pagination">
        <button className="tds-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
          .reduce((acc, p, idx, arr) => {
            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
            acc.push(p);
            return acc;
          }, [])
          .map((p, idx) =>
            p === '...'
              ? <span key={`e${idx}`} className="tds-ellipsis">…</span>
              : <button key={p} className={`tds-page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
          )}
        <button className="tds-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
      </div>
    );
  };

  const TABS = [
    { id: "completed",  label: "✅ Completed",  count: totals.completed },
    { id: "incomplete", label: "⏳ Incomplete", count: totals.pending + totals.started },
    { id: "missed",     label: "❌ Missed",     count: totals.missed },
  ];

  // Show full-screen loader only on initial load
  if (loading && !user) return (
    <div className="tds-loading">
      <div className="tds-spinner" />
      <p>Loading tasks...</p>
    </div>
  );

  if (!user) return <p className="tds-empty">User not found</p>;

  return (
    <div className="tds-container">

      {/* Header */}
      <div className="tds-header">
        <button className="tds-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div>
          <h1 className="tds-title">{user.name}</h1>
          <p className="tds-subtitle">Task Details</p>
        </div>
      </div>

      {/* User Info */}
      <div className="tds-user-card">
        <div className="tds-user-item"><span className="tds-user-label">Email</span><span>{user.email}</span></div>
        <div className="tds-user-item"><span className="tds-user-label">Mobile</span><span>{user.mobile}</span></div>
        <div className="tds-user-item"><span className="tds-user-label">Total</span><span className="tds-stat-total">{totals.total}</span></div>
        <div className="tds-user-item"><span className="tds-user-label">Completed</span><span className="tds-stat-done">{totals.completed}</span></div>
        <div className="tds-user-item"><span className="tds-user-label">Pending</span><span className="tds-stat-pending">{totals.pending}</span></div>
        <div className="tds-user-item"><span className="tds-user-label">Started</span><span className="tds-stat-started">{totals.started}</span></div>
        <div className="tds-user-item"><span className="tds-user-label">Missed</span><span className="tds-stat-missed">{totals.missed}</span></div>
      </div>

      {/* Tabs */}
      <div className="tds-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`tds-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
            <span className="tds-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Tab loading overlay */}
      {loading && user && (
        <div className="tds-tab-loading">
          <div className="tds-spinner" />
        </div>
      )}

      {/* ✅ Completed Tab */}
      {!loading && activeTab === "completed" && (
        <div className="tds-section">
          {completedTasks.length === 0 ? (
            <p className="tds-empty">No completed tasks</p>
          ) : (
            <>
              <div className="tds-table-wrap">
                <table className="tds-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Task Name</th>
                      <th>Coins Earned</th>
                      <th>Started At</th>
                      <th>Completed At</th>
                      <th>Proof</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedTasks.map((task, i) => (
                      <tr key={task.taskId}>
                        <td>{(currentPage - 1) * 10 + i + 1}</td>
                        <td>{task.taskName}</td>
                        <td>{renderCoin(task.coinsEarned)}</td>
                        <td>{formatDateTime(task.startedAt)}</td>
                        <td>{formatDateTime(task.completedAt)}</td>
                        <td>
                          {task.completionImage?.url ? (
                            <img
                              src={task.completionImage.url}
                              alt="Proof"
                              className="tds-thumb"
                              loading="lazy"
                              onClick={() => setPreviewImage(task.completionImage.url)}
                            />
                          ) : <span className="tds-no-img">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginator page={currentPage} setPage={setCurrentPage} />
            </>
          )}
        </div>
      )}

      {/* ⏳ Incomplete Tab */}
      {!loading && activeTab === "incomplete" && (
        <div className="tds-section">
          {incompleteTasks.length === 0 ? (
            <p className="tds-empty">No incomplete tasks</p>
          ) : (
            <>
              <div className="tds-table-wrap">
                <table className="tds-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Task Name</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Target Dist.</th>
                      <th>Current Dist.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incompleteTasks.map((task, i) => (
                      <tr key={task.taskId}>
                        <td>{(currentPage - 1) * 10 + i + 1}</td>
                        <td>{task.taskName}</td>
                        <td><span className="tds-type-badge">{task.taskType}</span></td>
                        <td><span className={`tds-status tds-status-${task.status}`}>{task.status}</span></td>
                        <td>
                          <div className="tds-progress-wrap">
                            <div className="tds-progress-bar">
                              <div className="tds-progress-fill" style={{ width: `${task.progress || 0}%` }} />
                            </div>
                            <span>{task.progress || 0}%</span>
                          </div>
                        </td>
                        <td>{task.targetDistance || "—"}</td>
                        <td>{task.currentDistance || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginator page={currentPage} setPage={setCurrentPage} />
            </>
          )}
        </div>
      )}

      {/* ❌ Missed Tab */}
      {!loading && activeTab === "missed" && (
        <div className="tds-section">
          {missedTasks.length === 0 ? (
            <p className="tds-empty">No missed tasks</p>
          ) : (
            <>
              <div className="tds-table-wrap">
                <table className="tds-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Task Name</th>
                      <th>Type</th>
                      <th>Scheduled Date</th>
                      <th>Coins Lost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missedTasks.map((task, i) => (
                      <tr key={task.taskId || i}>
                        <td>{(currentPage - 1) * 10 + i + 1}</td>
                        <td>{task.taskName}</td>
                        <td><span className="tds-type-badge">{task.taskType}</span></td>
                        <td>{formatDate(task.scheduledDate)}</td>
                        <td>{renderCoin(task.coinsLost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginator page={currentPage} setPage={setCurrentPage} />
            </>
          )}
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="tds-modal" onClick={() => setPreviewImage(null)}>
          <div className="tds-modal-content" onClick={e => e.stopPropagation()}>
            <img src={previewImage} alt="Preview" className="tds-modal-img" />
            <button className="tds-modal-close" onClick={() => setPreviewImage(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetailScreen;